import React, { useState, useEffect } from 'react';
import { updateProduct, getProducts, getCategories } from '../firebase/services';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUser } from '../Context/UserContext';
import BilingualInput from '../Components/BilingualInput';
import toast from 'react-hot-toast';
import { resizeImage } from '../Utils/imageHelpers';
import './Admin.css';

export default function EditProductAdmin() {
  const { productId } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { role, loading: userLoading } = useUser();

  const returnPage = Math.max(1, Number.parseInt(searchParams.get('returnPage') || '1', 10) || 1);
  const returnToProducts = `/admin/products${returnPage > 1 ? `?page=${returnPage}` : ''}`;

  useEffect(() => {
    if (!userLoading && role !== 'admin') {
      toast.error("Access denied. Admins only.");
      navigate(returnToProducts);
    }
  }, [role, userLoading, navigate, returnToProducts]);

  const [formData, setFormData] = useState({
    name: '', nameEn: '', nameAr: '',
    description: '', descriptionEn: '', descriptionAr: '',
    price: '', stock: '', 
    categories: [], 
    tags: '',
    isBestSeller: false, isFeatured: false, requiresPrescription: false,
    imageUrls: []
  });
  
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats);

        const products = await getProducts();
        const product = products.find(p => p.id === productId);
        
        if (product) {
          let nameEn = product.nameEn || '';
          let nameAr = product.nameAr || '';
          
          if (!nameEn && !nameAr) {
             if (typeof product.name === 'string') {
                const parts = product.name.split(' / ');
                nameEn = parts[0]?.trim() || '';
                nameAr = parts[1]?.trim() || '';
             } else if (typeof product.name === 'object' && product.name !== null) {
                nameEn = product.name.en || '';
                nameAr = product.name.ar || '';
             }
          }

          let descriptionEn = product.descriptionEn || '';
          let descriptionAr = product.descriptionAr || '';

          if (!descriptionEn && !descriptionAr) {
             if (typeof product.description === 'string') {
                const parts = product.description.split('\n\n---\n\n');
                descriptionEn = parts[0]?.trim() || '';
                descriptionAr = parts[1]?.trim() || '';
             } else if (typeof product.description === 'object' && product.description !== null) {
                descriptionEn = product.description.en || '';
                descriptionAr = product.description.ar || '';
             }
          }

          let productCategories = product.categories || [];
          if (productCategories.length === 0 && product.category) {
            productCategories = [product.category];
          }
          
          // Convert category names to IDs if necessary
          const categoryIds = productCategories.map(catNameOrId => {
             // If it looks like an ID (alphanumeric, no spaces usually, but names can be anything)
             // Check if it matches an existing ID
             const exactIdMatch = cats.find(c => c.id === catNameOrId);
             if (exactIdMatch) return catNameOrId;
             
             // Try to find by name
             const nameMatch = cats.filter(c => c.name.toLowerCase() === catNameOrId.toLowerCase());
             if (nameMatch.length > 0) {
                // If multiple matches (ambiguous), return all IDs to be safe
                // But map expects one return. We'll flatten later.
                return nameMatch.map(c => c.id);
             }
             return null;
          }).flat().filter(Boolean);

          // Remove duplicates
          const uniqueCategoryIds = [...new Set(categoryIds)];

          let productImages = product.imageUrls || [];
          if (productImages.length === 0) {
             if (product.images && product.images.length > 0) productImages = product.images;
             else if (product.imageUrl) productImages = [product.imageUrl];
          }

          setFormData({
            name: '',
            nameEn, nameAr,
            description: '',
            descriptionEn, descriptionAr,
            price: product.price || '',
            stock: product.stock || '',
            categories: uniqueCategoryIds,
            tags: product.tags ? product.tags.join(', ') : '',
            isBestSeller: product.isBestSeller || false,
            isFeatured: product.isFeatured || false,
            requiresPrescription: product.requiresPrescription || false,
            imageUrls: productImages
          });
        }
      } catch (error) {
        console.error("Failed to load product", error);
        toast.error("Failed to load product data");
      }
    };

    loadData();
  }, [productId]);

  const handleChange = (fieldNameOrEvent, value) => {
    if (typeof fieldNameOrEvent === 'string' && value !== undefined) {
      setFormData(prev => ({ ...prev, [fieldNameOrEvent]: value }));
    } else if (fieldNameOrEvent && fieldNameOrEvent.target) {
      const { name, value: eventValue, type, checked } = fieldNameOrEvent.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : eventValue
      }));
    }
  };

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentCats = prev.categories || [];
      if (checked) {
        return { ...prev, categories: [...currentCats, value] };
      } else {
        return { ...prev, categories: currentCats.filter(c => c !== value) };
      }
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      try {
        const resizedFiles = await Promise.all(files.map(file => resizeImage(file, 800, 800)));
        setNewImageFiles(prev => [...prev, ...resizedFiles]);
        
        const newPreviews = resizedFiles.map(file => URL.createObjectURL(file));
        setNewImagePreviews(prev => [...prev, ...newPreviews]);
      } catch (error) {
        console.error("Error resizing images:", error);
        toast.error("Failed to process images");
      }
    }
  };

  const removeNewImage = (index) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));
  };

  const getCombinedName = () => {
    const en = formData.nameEn?.trim() || '';
    const ar = formData.nameAr?.trim() || '';
    return [en, ar].filter(Boolean).join(' / ');
  };

  const getCombinedDescription = () => {
    const en = formData.descriptionEn?.trim() || '';
    const ar = formData.descriptionAr?.trim() || '';
    return [en, ar].filter(Boolean).join('\n\n---\n\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const combinedName = getCombinedName();
    if (!combinedName) return toast.error("Product Name (EN or AR) is required");
    if (formData.categories.length === 0) return toast.error("Please select at least one category");
    if (formData.imageUrls.length === 0 && newImageFiles.length === 0) return toast.error("Please select at least one image");
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        name: combinedName,
        description: getCombinedDescription(),
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };
      
      await updateProduct(productId, submitData, newImageFiles);
      toast.success("Product updated successfully!");
      navigate(returnToProducts);
    } catch (error) {
      toast.error("Error updating product: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-card">
        <div className="admin-header">
          <h2>Edit Product</h2>
          <p>Update product information</p>
        </div>

        <form onSubmit={handleSubmit}>
          
          <div className="section-title">
            <ion-icon name="document-text-outline"></ion-icon> Product Information
          </div>
          
          <BilingualInput
            label="Product Name"
            fieldNameEn="nameEn"
            fieldNameAr="nameAr"
            valueEn={formData.nameEn}
            valueAr={formData.nameAr}
            onChange={handleChange}
            placeholder="Enter product name"
            required
          />

          <div className="section-title" style={{marginTop: '30px'}}>
            <ion-icon name="document-text-outline"></ion-icon> Description
          </div>
          
          <BilingualInput
            label="Product Description"
            fieldNameEn="descriptionEn"
            fieldNameAr="descriptionAr"
            valueEn={formData.descriptionEn}
            valueAr={formData.descriptionAr}
            onChange={handleChange}
            placeholder="Enter product description"
            isTextarea={true}
            required
          />

          <div className="form-row">
            <div className="form-group">
              <label>Price (EGP)</label>
              <input
                type="number"
                className="custom-input"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="form-group">
              <label>Stock Quantity</label>
              <input
                type="number"
                className="custom-input"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                min="0"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Categories (Select multiple)</label>
            <div className="category-selection-container">
              {categories.length > 0 ? (
                (() => {
                  const parents = categories.filter(c => !c.parentId);
                  const children = categories.filter(c => c.parentId);
                  
                  return parents.map(parent => (
                    <div key={parent.id} className="category-group">
                      <div className="category-parent">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            className="styled-checkbox"
                            value={parent.id}
                            checked={formData.categories.includes(parent.id)}
                            onChange={handleCategoryChange}
                          />
                          {parent.name}
                        </label>
                      </div>
                      <div className="category-children">
                        {children.filter(c => c.parentId === parent.id).map(child => (
                          <div key={child.id} className="category-child">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                className="styled-checkbox"
                                value={child.id}
                                checked={formData.categories.includes(child.id)}
                                onChange={handleCategoryChange}
                              />
                              {child.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()
              ) : (
                <p style={{padding: '20px', textAlign: 'center', color: '#6b7280'}}>No categories found.</p>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Tags (Comma separated)</label>
            <input
              type="text"
              className="custom-input"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="e.g. Dog, Food, Dry, Puppy"
            />
            <small className="helper-text">Used for "You may like" recommendations.</small>
          </div>

          <div className="section-title" style={{marginTop: '30px'}}>
            <ion-icon name="images-outline"></ion-icon> Product Images
          </div>

          <div className="form-group">
            <label>Current Images</label>
            <div className="image-previews">
              {formData.imageUrls.map((src, index) => (
                <div key={`existing-${index}`} className="image-preview-container">
                  <img src={src} alt={`Existing ${index}`} className="image-preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeExistingImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Add New Images</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="file-input"
            />
            <div className="image-previews">
              {newImagePreviews.map((src, index) => (
                <div key={`new-${index}`} className="image-preview-container">
                  <img src={src} alt={`New Preview ${index}`} className="image-preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeNewImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="section-title" style={{marginTop: '30px'}}>
            <ion-icon name="options-outline"></ion-icon> Options
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isBestSeller"
                  checked={formData.isBestSeller}
                  onChange={handleChange}
                />
                Best Seller
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                />
                Featured Product
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="requiresPrescription"
                  checked={formData.requiresPrescription}
                  onChange={handleChange}
                />
                Requires Prescription
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate(returnToProducts)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Updating Product...' : 'Update Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
